import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Check, ChevronsUpDown, Search, X, User } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

/**
 * Combobox buscable de empleados.
 *
 * Props:
 *  - employees: Array de empleados
 *  - value: id del empleado seleccionado (string | null)
 *  - onValueChange: (employee) => void  -> recibe el objeto empleado (no solo el id)
 *  - placeholder: texto del placeholder
 *  - disabled: si está deshabilitado
 *  - className: estilos extra para el trigger
 *
 * Filtra por: nombre, apellido, cédula, rol, lavandería.
 */
const EmployeeCombobox = ({
    employees = [],
    value,
    onValueChange,
    placeholder = 'Selecciona un empleado…',
    disabled = false,
    className,
}) => {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');
    const [highlighted, setHighlighted] = useState(0);
    const inputRef = useRef(null);
    const listRef = useRef(null);

    // Empleado actualmente seleccionado
    const selected = useMemo(
        () => employees.find((e) => e._id === value) || null,
        [employees, value]
    );

    // Filtrado case-insensitive y sin acentos
    const normalize = (s) =>
        (s || '')
            .toString()
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .toLowerCase()
            .trim();

    const filtered = useMemo(() => {
        const q = normalize(query);
        if (!q) return employees;
        return employees.filter((emp) => {
            const fullName = `${emp.nombre || ''} ${emp.apellido || ''}`;
            const lavanderia = emp.lavanderia?.nombre || '';
            return (
                normalize(fullName).includes(q) ||
                normalize(emp.cedula).includes(q) ||
                normalize(emp.rol).includes(q) ||
                normalize(lavanderia).includes(q)
            );
        });
    }, [employees, query]);

    // Cuando se abre el popover o cambia la query, enfocar el input
    useEffect(() => {
        if (open) {
            // Pequeño timeout para que el input ya esté en el DOM
            setTimeout(() => inputRef.current?.focus(), 10);
            setHighlighted(0);
        } else {
            setQuery('');
        }
    }, [open]);

    // Mantener highlighted visible al hacer scroll
    useEffect(() => {
        const el = listRef.current?.querySelector(`[data-idx="${highlighted}"]`);
        if (el) el.scrollIntoView({ block: 'nearest' });
    }, [highlighted]);

    const handleSelect = (emp) => {
        onValueChange?.(emp);
        setOpen(false);
    };

    const handleClear = (e) => {
        e.stopPropagation();
        onValueChange?.(null);
    };

    const handleKeyDown = (e) => {
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setHighlighted((h) => Math.min(h + 1, filtered.length - 1));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setHighlighted((h) => Math.max(h - 1, 0));
        } else if (e.key === 'Enter') {
            e.preventDefault();
            if (filtered[highlighted]) handleSelect(filtered[highlighted]);
        } else if (e.key === 'Escape') {
            if (query) {
                setQuery('');
            } else {
                setOpen(false);
            }
        }
    };

    return (
        // modal: dentro de un Dialog, Radix pone pointer-events:none en el body y el
        // popover (portal fuera del diálogo) lo hereda: la lista se ve pero los clics
        // la atraviesan. En modo modal el popover es la capa activa y recibe el puntero.
        <Popover open={open} onOpenChange={setOpen} modal>
            <PopoverTrigger asChild>
                <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={open}
                    disabled={disabled}
                    className={cn(
                        'w-full justify-between h-auto min-h-[2.5rem] px-3 py-2 font-normal',
                        !selected && 'text-muted-foreground',
                        className
                    )}
                >
                    {selected ? (
                        <div className="flex items-center gap-2 min-w-0 flex-1 text-left">
                            <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                                <span className="text-xs font-semibold">
                                    {(selected.nombre?.[0] || '?').toUpperCase()}
                                </span>
                            </div>
                            <div className="min-w-0 flex-1">
                                <div className="truncate text-sm font-medium text-foreground">
                                    {selected.nombre} {selected.apellido}
                                </div>
                                <div className="truncate text-xs text-muted-foreground">
                                    {selected.cedula} {selected.rol ? `• ${selected.rol}` : ''}
                                </div>
                            </div>
                        </div>
                    ) : (
                        <span className="flex items-center gap-2">
                            <User className="h-4 w-4 shrink-0" />
                            {placeholder}
                        </span>
                    )}
                    <div className="flex items-center gap-1 shrink-0">
                        {selected && !disabled && (
                            <span
                                role="button"
                                tabIndex={0}
                                aria-label="Limpiar selección"
                                onClick={handleClear}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        handleClear(e);
                                    }
                                }}
                                className="rounded p-0.5 hover:bg-muted cursor-pointer"
                            >
                                <X className="h-3.5 w-3.5 text-muted-foreground" />
                            </span>
                        )}
                        <ChevronsUpDown className="h-4 w-4 text-muted-foreground" />
                    </div>
                </Button>
            </PopoverTrigger>
            <PopoverContent
                className="w-[var(--radix-popover-trigger-width)] p-0"
                align="start"
                onOpenAutoFocus={(e) => e.preventDefault()}
            >
                <div className="flex items-center border-b px-3 py-2 gap-2">
                    <Search className="h-4 w-4 shrink-0 text-muted-foreground" />
                    <Input
                        ref={inputRef}
                        value={query}
                        onChange={(e) => {
                            setQuery(e.target.value);
                            setHighlighted(0);
                        }}
                        onKeyDown={handleKeyDown}
                        placeholder="Buscar por nombre, cédula, rol o lavandería…"
                        className="h-8 border-0 shadow-none focus-visible:ring-0 px-0"
                    />
                    {query && (
                        <button
                            type="button"
                            onClick={() => {
                                setQuery('');
                                inputRef.current?.focus();
                            }}
                            className="text-muted-foreground hover:text-foreground"
                            aria-label="Limpiar búsqueda"
                        >
                            <X className="h-3.5 w-3.5" />
                        </button>
                    )}
                </div>
                <div
                    ref={listRef}
                    className="max-h-[320px] overflow-y-auto overscroll-contain"
                >
                    {filtered.length === 0 ? (
                        <div className="px-3 py-8 text-center text-sm text-muted-foreground">
                            No se encontraron empleados.
                        </div>
                    ) : (
                        <>
                            {filtered.length > 1 && (
                                <div className="px-3 py-1.5 text-[11px] uppercase tracking-wide text-muted-foreground bg-muted/40 border-b">
                                    {filtered.length} empleado{filtered.length === 1 ? '' : 's'}
                                </div>
                            )}
                            <ul role="listbox" className="py-1">
                                {filtered.map((emp, idx) => {
                                    const isSelected = selected?._id === emp._id;
                                    const isHighlighted = idx === highlighted;
                                    return (
                                        <li
                                            key={emp._id}
                                            data-idx={idx}
                                            role="option"
                                            aria-selected={isSelected}
                                            onMouseEnter={() => setHighlighted(idx)}
                                            // onPointerDown y NO onClick. Con onClick el clic nunca llega a este <li>:
// algo del árbol de capas de Radix cancela el evento en mousedown, con lo cual
// el navegador ni siquiera genera el click y React nunca lo despacha — el
// popover se cierra pero no se selecciona nada. El pointerdown sí llega, porque
// ocurre antes de que nada se desmonte. El preventDefault impide además que el
// clic siga de largo hacia el diálogo de fondo y lo cierre.
onPointerDown={(e) => {
                                                e.preventDefault();
                                                handleSelect(emp);
                                            }}
                                            className={cn(
                                                'flex items-center gap-3 px-3 py-2 cursor-pointer text-sm',
                                                isHighlighted && 'bg-accent',
                                                isSelected && 'bg-primary/5'
                                            )}
                                        >
                                            <div
                                                className={cn(
                                                    'flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-semibold',
                                                    isSelected
                                                        ? 'bg-primary text-primary-foreground'
                                                        : 'bg-muted text-muted-foreground'
                                                )}
                                            >
                                                {(emp.nombre?.[0] || '?').toUpperCase()}
                                            </div>
                                            <div className="min-w-0 flex-1">
                                                <div className="truncate font-medium text-foreground">
                                                    {emp.nombre} {emp.apellido}
                                                </div>
                                                <div className="truncate text-xs text-muted-foreground">
                                                    {emp.cedula}
                                                    {emp.rol ? ` • ${emp.rol}` : ''}
                                                    {emp.lavanderia?.nombre
                                                        ? ` • ${emp.lavanderia.nombre}`
                                                        : ''}
                                                </div>
                                            </div>
                                            {isSelected && (
                                                <Check className="h-4 w-4 shrink-0 text-primary" />
                                            )}
                                        </li>
                                    );
                                })}
                            </ul>
                        </>
                    )}
                </div>
            </PopoverContent>
        </Popover>
    );
};

export default EmployeeCombobox;
